import type {
  BenchTier as TierData,
  GeomEntity,
  Geometry,
  InnerWall,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  computeBounds,
  FEATURE_COLOR,
} from './geometry';
import { wallThicknessOf } from './dent';
import { benchThickness } from './heights';

/**
 * Per-side editable bench model for saunas and steam rooms.
 *
 * A room starts with automatically generated benches. The moment the user edits
 * a side (changes its depth, removes it, or draws it with the bench tool) that
 * side becomes a manual override stored on `inst.benchEdits`; untouched sides
 * keep following the automatic layout. Once any side is overridden the room is
 * rendered from this single-tier per-side leg model.
 */

export type BenchSide = 'back' | 'left' | 'right';
export const BENCH_SIDES: BenchSide[] = ['back', 'left', 'right'];

export type BenchTier = 'low' | 'medium' | 'high';

/** A bench leg/tier: depth (toward the centre), span along the wall, optional id + tier. */
export interface BenchLeg {
  id?: string;
  depth: number;
  start: number;
  end: number;
  tier?: BenchTier;
  /** Explicit fill colour for this bench; overrides the room/tier colour. */
  color?: string;
  /** Slat (doghe) composition across the bench depth: widths (mm, mixed allowed)
   *  and the gaps between them (length = slats.length - 1). When present the
   *  depth is driven by them (Σ slats + Σ gaps). */
  slats?: number[];
  gaps?: number[];
  slatPresetId?: string;
  /** Per-slat thickness (mm), parallel to `slats`; the bench slab thickness is
   *  the thickest slat (see `benchThickness`). */
  slatThickness?: number[];
  /** Millimetres this bench is covered by the bench above it (visible plan depth
   *  = depth - overlap). Ignored for the wall-touching tier (index 0). */
  overlap?: number;
  /** Per-bench seat height (mm) override; when absent the sauna/tier default
   *  (`benchTierHeights`) is used. Used by the 3D extrusion and sections. */
  height?: number;
  /** Legacy per-bench seat-slab thickness (mm), superseded by `slatThickness`. */
  thickness?: number;
}

/** How far a tier tucks behind the frame front of the tier toward the wall by
 *  default (mm): cavalletto.dxf's 56 — the upper skirting's board and batten
 *  then meet its trestle's front post. */
export const DEFAULT_FRAME_TUCK = 56;

/** How far a bench's frame front stands behind its front face (mm): past its
 *  front board, the last of two or more slats; nothing with fewer. */
export function frameFrontInset(leg: Pick<BenchLeg, 'depth' | 'slats' | 'gaps'>): number {
  const spans = benchSlatSpans(leg);
  if (spans.length < 2) return 0;
  const D = leg.depth > 0 ? leg.depth : 1;
  return Math.max(0, D - Math.min(D, spans[spans.length - 1].off));
}

/** A tier's overlap when it has none of its own: {@link DEFAULT_FRAME_TUCK}
 *  behind the frame front of `upper`, the tier it tucks under. */
export function defaultOverlap(upper: Pick<BenchLeg, 'depth' | 'slats' | 'gaps'> | null | undefined): number {
  return DEFAULT_FRAME_TUCK + (upper ? frameFrontInset(upper) : 0);
}

/** Depth (mm) implied by a bench's slats + gaps, or null when it has no slats. */
export function benchDepthFromSlats(leg: Pick<BenchLeg, 'slats' | 'gaps'>): number | null {
  if (!leg.slats || leg.slats.length === 0) return null;
  const s = leg.slats.reduce((a, b) => a + Math.max(0, b), 0);
  const g = (leg.gaps ?? []).reduce((a, b) => a + Math.max(0, b), 0);
  return s + g;
}

/** Effective overlap (mm) of a bench: 0 for the wall-touching tier (index 0),
 *  else its own value, by default {@link defaultOverlap} under `upper`. */
export function benchOverlap(leg: Pick<BenchLeg, 'overlap'>, index: number, upper?: Pick<BenchLeg, 'depth' | 'slats' | 'gaps'> | null): number {
  if (index <= 0) return 0;
  return Math.max(0, leg.overlap ?? defaultOverlap(upper));
}

/** Callbacks describing one wall stack of `n` tiers (index 0 = against the wall)
 *  to {@link cumulativeTuck}. Kept generic so the rectangular and polygonal room
 *  bench models share one tuck algorithm. */
export interface TuckSpec {
  /** Tier level; higher = taller. A tier tucks only under a strictly-higher one. */
  rank: (k: number) => number;
  /** Whether tier k is a slab (has a thickness) — only then can a lower tier slide
   *  under it; a solid-to-floor tier is butted against instead. */
  hasSlab: (k: number) => boolean;
  /** Overlap (mm) tier k slides under the one toward the wall (k >= 1). */
  overlap: (k: number) => number;
  /** Furthest (mm) tier k may slide wall-ward — the natural depth to the wall, so
   *  a bench never runs past the wall behind it (nor a same-level neighbour). */
  maxOffset: (k: number) => number;
  /** Tier k's tuck set outright from the one before it (`prev`), or null to take
   *  the overlap rule — two profiled tiers meet where their profiles say. */
  fixed?: (k: number, prev: number) => number | null;
}

/**
 * Cumulative wall-ward tuck (mm) per tier in one wall stack (index 0 = against the
 * wall, highest). A tier runs UNDER the bench toward the wall by its `overlap`,
 * but only when that higher bench is STRICTLY higher and is a slab (has a
 * thickness) — the slab then has a void beneath it for the lower seat to slide
 * into. Without a thickness the higher bench is solid to the floor, so the lower
 * one runs UP TO its face instead (no tuck). The tuck stops at whichever comes
 * first: the wall's inner face or the next bench at the same height. Shared by the
 * rectangular ({@link benchStackShifts}) and polygonal (edgeTuckShifts) room bench
 * models, plan/section/3D, so the overlap reads the same everywhere.
 */
export function cumulativeTuck(n: number, spec: TuckSpec): number[] {
  const shifts: number[] = [];
  let cum = 0;
  for (let k = 0; k < n; k++) {
    if (k > 0) {
      const canTuck = spec.rank(k - 1) > spec.rank(k) && spec.hasSlab(k - 1);
      const want = spec.fixed?.(k, cum) ?? cum + (canTuck ? spec.overlap(k) : 0);
      cum = Math.max(0, Math.min(want, spec.maxOffset(k)));
    }
    shifts.push(cum);
  }
  return shifts;
}

/** Wall-ward tuck per rectangular-room tier (see {@link cumulativeTuck}). */
export function benchStackShifts(legs: BenchLeg[]): number[] {
  const count = legs.length;
  // Prefix sum of depths: tier k's natural wall-side depth is its furthest tuck.
  const start: number[] = [];
  let acc = 0;
  for (let k = 0; k < count; k++) { start[k] = acc; acc += Math.max(0, legs[k].depth); }
  return cumulativeTuck(count, {
    rank: (k) => tierRank(effectiveTier(legs[k], k, count)),
    hasSlab: (k) => benchThickness(legs[k]) != null,
    overlap: (k) => benchOverlap(legs[k], k, legs[k - 1]),
    maxOffset: (k) => start[k],
  });
}

/** Translate a box wall-ward (away from the room centre, toward the wall opposite
 *  the inner edge `innerVal` on axis `innerAxis`) by `shift` mm — the overlap tuck. */
export function tuckBoxWallward(box: Box, innerAxis: 'x' | 'y', innerVal: number, shift: number): Box {
  if (shift <= 0) return box;
  const yAxis = innerAxis === 'y';
  const lo = yAxis ? box.y0 : box.x0;
  const hi = yAxis ? box.y1 : box.x1;
  const wall = Math.abs(innerVal - lo) < Math.abs(innerVal - hi) ? hi : lo; // wall edge = opposite the centre-facing edge
  const dir = Math.sign(innerVal - wall) || 1; // wall → centre
  const d = -dir * shift; // toward the wall
  return yAxis ? { ...box, y0: box.y0 + d, y1: box.y1 + d } : { ...box, x0: box.x0 + d, x1: box.x1 + d };
}

/** Outer-wall bench tier boxes with the overlap tuck applied, so the section and
 *  3D views slide a lower bench under the higher one exactly as the plan does. */
export function benchTierBoxesTucked(inst: ProductInstance): TierBox[] {
  return tuckTierGroups(benchTierBoxes(inst), (b) => b.side);
}

/** Inner-wall bench tier boxes with the overlap tuck applied (grouped per wall face). */
export function innerWallTierBoxesTucked(inst: ProductInstance): InnerTierBox[] {
  return tuckTierGroups(innerWallTierBoxes(inst), (b) => `${b.iwIndex}:${b.face}`);
}

/** Group tier boxes by wall, order them wall→centre, and slide each wall-ward by
 *  its cumulative overlap tuck. Generic over the outer and inner tier-box types. */
function tuckTierGroups<T extends { index: number; leg: BenchLeg; box: Box; innerAxis: 'x' | 'y'; innerVal: number }>(
  boxes: T[],
  keyOf: (b: T) => string,
): T[] {
  const groups = new Map<string, T[]>();
  for (const b of boxes) {
    const key = keyOf(b);
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(b);
  }
  const out: T[] = [];
  for (const group of groups.values()) {
    group.sort((a, b) => a.index - b.index);
    const shifts = benchStackShifts(group.map((g) => g.leg));
    group.forEach((tb, k) => out.push({ ...tb, box: tuckBoxWallward(tb.box, tb.innerAxis, tb.innerVal, shifts[k]) }));
  }
  return out;
}

/** Slat spans along the bench depth, measured from the WALL edge (offset 0). Each
 *  entry is a filled slat; the gaps between them are the empty spaces. The gap for
 *  a slat sits on its WALL side (before it): `gaps[i]` is the gap between slat i
 *  and whatever is toward the wall — so the first gap is between the wall and the
 *  first slat, the second between the first and second slat, and so on. */
export function benchSlatSpans(leg: Pick<BenchLeg, 'slats' | 'gaps'>): Array<{ off: number; width: number }> {
  const out: Array<{ off: number; width: number }> = [];
  const slats = leg.slats ?? [];
  const gaps = leg.gaps ?? [];
  let off = 0;
  for (let i = 0; i < slats.length; i++) {
    off += Math.max(0, gaps[i] ?? 0); // gap before slat i (the first is against the wall)
    out.push({ off, width: Math.max(0, slats[i]) });
    off += Math.max(0, slats[i]);
  }
  return out;
}

/** Fill colour for each sauna bench tier (steam benches use a single colour). */
export const BENCH_TIER_COLORS: Record<BenchTier, string> = {
  high: '#d5c6b2',
  medium: '#f2d19f',
  low: '#f4e8ce',
};
export const STEAM_BENCH_COLOR = '#987349';

/** Steam-room bench tier colours (high against the wall → low toward the centre). */
export const STEAM_BENCH_TIER_COLORS: Record<BenchTier, string> = {
  high: '#99744b',
  medium: '#a68763',
  low: '#b8a082',
};

/**
 * The automatic tier for a bench by its position and how many tiers the side
 * has. The innermost (toward the centre) is always low; the rest fill upward:
 *  - 1 bench  → low
 *  - 2 benches → medium (wall), low (centre)
 *  - 3+ benches → high (wall), medium…, low (centre)
 */
export function autoTierFor(index: number, count: number): BenchTier {
  if (count <= 1) return 'low';
  if (index === count - 1) return 'low'; // innermost, toward the centre
  if (index === 0) return count === 2 ? 'medium' : 'high'; // against the wall
  return 'medium';
}

/** A tier's effective level: an explicit override, else the automatic tier. */
export function effectiveTier(leg: BenchLeg, index: number, count: number): BenchTier {
  return leg.tier ?? autoTierFor(index, count);
}

export const MIN_BENCH_DEPTH = 200;

interface Interior {
  ix0: number;
  ix1: number;
  iy0: number;
  iy1: number;
}

export function benchInterior(inst: ProductInstance): Interior {
  const w = wallThicknessOf(inst);
  return {
    ix0: -inst.width / 2 + w,
    ix1: inst.width / 2 - w,
    iy0: -inst.depth / 2 + w,
    iy1: inst.depth / 2 - w,
  };
}

/** Whether the user has manually edited this room's benches. */
export function hasBenchEdits(inst: ProductInstance): boolean {
  return !!inst.benchEdits && Object.keys(inst.benchEdits).length > 0;
}

/** Whether the room is managed by the per-side tier model (vs the auto layout). */
export function usesLegModel(inst: ProductInstance): boolean {
  return inst.benchTiers !== undefined || hasBenchEdits(inst) || !!inst.cubePos;
}

/**
 * The resolved bench tiers for a side, outermost (wall) → innermost (centre).
 * Benches are never auto-generated: a room has only the tiers the user added
 * (via `benchTiers`, or the legacy single-leg `benchEdits`). A room with
 * neither has no benches.
 */
export function resolveTiers(inst: ProductInstance, side: BenchSide): BenchLeg[] {
  if (inst.benchTiers !== undefined) return inst.benchTiers[side] ?? [];
  if (inst.benchEdits && side in inst.benchEdits) {
    const e = inst.benchEdits[side];
    return e ? [e] : [];
  }
  return [];
}

/** Total depth of all tiers on a side (toward the centre). */
export function sideTotalDepth(inst: ProductInstance, side: BenchSide): number {
  return resolveTiers(inst, side).reduce((s, t) => s + t.depth, 0);
}

/** Promote the room to the managed tier model, snapshotting any current benches. */
function ensureManaged(inst: ProductInstance): void {
  if (inst.benchTiers !== undefined) return;
  const bt: NonNullable<ProductInstance['benchTiers']> = {};
  for (const side of BENCH_SIDES) {
    const t = resolveTiers(inst, side);
    if (t.length) bt[side] = t.map((x) => ({ ...x, id: x.id ?? newBenchId() }));
  }
  inst.benchTiers = bt;
  inst.benchEdits = undefined; // superseded by benchTiers
}

/** The outermost tier per side (for the steam-cube sizing and legacy callers). */
export function resolveLegs(inst: ProductInstance): Record<BenchSide, BenchLeg | null> {
  const out = {} as Record<BenchSide, BenchLeg | null>;
  for (const side of BENCH_SIDES) out[side] = resolveTiers(inst, side)[0] ?? null;
  return out;
}

function rect(x0: number, y0: number, x1: number, y1: number): Extract<GeomEntity, { kind: 'polyline' }> {
  return {
    kind: 'polyline',
    closed: true,
    points: [
      { x: x0, y: y0 },
      { x: x1, y: y0 },
      { x: x1, y: y1 },
      { x: x0, y: y1 },
    ],
  };
}

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}


/** Subtract a hole rectangle from an outer rectangle, returning the slabs that remain. */
function subtractBox(o: Box, h: Box): Box[] {
  const ix0 = Math.max(o.x0, h.x0);
  const iy0 = Math.max(o.y0, h.y0);
  const ix1 = Math.min(o.x1, h.x1);
  const iy1 = Math.min(o.y1, h.y1);
  if (ix1 <= ix0 + 1 || iy1 <= iy0 + 1) return [o]; // no meaningful overlap
  const out: Box[] = [];
  if (o.y1 - iy1 > 1) out.push({ x0: o.x0, y0: iy1, x1: o.x1, y1: o.y1 }); // top
  if (iy0 - o.y0 > 1) out.push({ x0: o.x0, y0: o.y0, x1: o.x1, y1: iy0 }); // bottom
  if (ix0 - o.x0 > 1) out.push({ x0: o.x0, y0: iy0, x1: ix0, y1: iy1 }); // left
  if (o.x1 - ix1 > 1) out.push({ x0: ix1, y0: iy0, x1: o.x1, y1: iy1 }); // right
  return out;
}

/**
 * Carve a heater footprint `h` from a bench tier so it splits the bench only
 * ALONG ITS LENGTH, never reducing its depth. The hole is first extended across
 * the bench's full depth (its short dimension, given by `shortAxis`) so the two
 * remaining segments keep the bench's full depth instead of leaving a thin strip
 * on the short side. A heater that doesn't actually overlap the bench is ignored.
 */
function carveAcrossDepth(b: Box, shortAxis: 'x' | 'y', h: Box): Box[] {
  const ox0 = Math.max(b.x0, h.x0), oy0 = Math.max(b.y0, h.y0);
  const ox1 = Math.min(b.x1, h.x1), oy1 = Math.min(b.y1, h.y1);
  if (ox1 <= ox0 + 1 || oy1 <= oy0 + 1) return [b]; // not actually on this bench
  const full: Box = shortAxis === 'y'
    ? { x0: h.x0, x1: h.x1, y0: b.y0 - 1, y1: b.y1 + 1 } // back/front: keep full depth (y)
    : { x0: b.x0 - 1, x1: b.x1 + 1, y0: h.y0, y1: h.y1 }; // left/right: keep full depth (x)
  return subtractBox(b, full);
}

function boxToPoly(b: Box): Extract<GeomEntity, { kind: 'polyline' }> {
  return rect(b.x0, b.y0, b.x1, b.y1);
}

/**
 * The steam feature-cube is no longer an auto-generated room feature: it is now a
 * standalone "Steam Cube" product the user drops into the room, which sits on top
 * of the benches and never carves them. Kept as a null-returning stub so callers
 * (bench carving, hit-testing) need no branching.
 */
export function featureBox(_inst: ProductInstance): Box | null {
  return null;
}

/** Legacy in-bench feature-cube hit-test; the cube is now a separate instance. */
export function featureAt(_inst: ProductInstance, _p: Vec2, _margin = 0): 'cube' | null {
  return null;
}

/** A resolved bench tier with its box and fill, for rendering and hit-testing. */
export interface TierBox {
  side: BenchSide;
  index: number;
  id: string;
  leg: BenchLeg;
  box: Box;
  fill: string;
  /** Inner edge (toward the centre): the axis and its coordinate. */
  innerAxis: 'x' | 'y';
  innerVal: number;
}

/** Fill colour for a bench tier: explicit leg colour, else room colour, else the
 *  tier-level palette (sauna or steam). */
export function benchFill(inst: ProductInstance, leg: BenchLeg, index: number, count: number): string {
  const isSteam = inst.category === 'steamroom';
  return leg.color ?? inst.benchColor ?? (isSteam ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS)[effectiveTier(leg, index, count)];
}

export type BenchCorner = 'left' | 'right';

/** Rank of a bench tier level: high (2), medium (1), low (0). */
export const tierRank = (t: BenchTier): number => (t === 'high' ? 2 : t === 'medium' ? 1 : 0);

/** Level rank of the tier at `index` in a stack of `count` (position-based, i.e.
 *  the automatic tier). Wall side is highest, innermost is lowest. Shared by the
 *  rectangular and polygonal room bench models so corners join by the same rule. */
export function autoTierRank(index: number, count: number): number {
  return tierRank(autoTierFor(index, count));
}

/** Whether the back/`corner` corner join at rank `level` has been manually
 *  swapped (the default winner of that tie is flipped). */
export function benchCornerJoinSwapped(inst: ProductInstance, corner: BenchCorner, level: number): boolean {
  return (inst.benchCornerJoins || []).some((j) => j.corner === corner && j.level === level);
}

/** Flip the winner of one rectangular-room bench corner join (add/remove override). */
export function toggleBenchCornerJoin(inst: ProductInstance, corner: BenchCorner, level: number): void {
  const list = inst.benchCornerJoins || (inst.benchCornerJoins = []);
  const idx = list.findIndex((j) => j.corner === corner && j.level === level);
  if (idx >= 0) list.splice(idx, 1);
  else list.push({ corner, level });
}

/**
 * Winner ('back' or the side) of a same-level tie at the back/`corner` corner:
 * the longer wall by default (back's wall is `width`, the side's is `depth`;
 * an exact tie favours back), flipped by a stored swap override.
 */
function cornerTieWinner(inst: ProductInstance, corner: BenchCorner, level: number): 'back' | BenchCorner {
  let win: 'back' | BenchCorner = inst.width >= inst.depth ? 'back' : corner;
  if (benchCornerJoinSwapped(inst, corner, level)) win = win === 'back' ? corner : 'back';
  return win;
}

/** Compute every bench tier's box (stacked inward, sides clamped to the back). */
function tierBoxesOf(inst: ProductInstance): TierBox[] {
  const { ix0, ix1, iy1 } = benchInterior(inst);
  const out: TierBox[] = [];
  const fillFor = (leg: BenchLeg, index: number, count: number): string => benchFill(inst, leg, index, count);

  const back = resolveTiers(inst, 'back');
  const left = resolveTiers(inst, 'left');
  const right = resolveTiers(inst, 'right');
  // L-shaped corners meet tier-by-tier BY LEVEL (high↔high, medium↔medium,
  // low↔low), whichever wall is benched first: a tier yields the corner to any
  // tier of a HIGHER level on the other wall. Where both walls carry the SAME
  // level (a tie — the common case of one bench per wall) one of them wins the
  // corner (spans through) and the other yields to it: by default the bench on
  // the longer wall, swappable per corner+level via `benchCornerJoins`.
  const backLv = back.map((leg, k) => tierRank(effectiveTier(leg, k, back.length)));
  const leftLv = left.map((leg, k) => tierRank(effectiveTier(leg, k, left.length)));
  const rightLv = right.map((leg, k) => tierRank(effectiveTier(leg, k, right.length)));
  // Overlap tuck per wall stack: a bench tucked under the one toward the wall
  // recedes, so a corner-facing bench must run in to meet its TUCKED inner edge —
  // the corner joins use tucked bounding boxes, not the natural ones.
  const backShift = benchStackShifts(back);
  const leftShift = benchStackShifts(left);
  const rightShift = benchStackShifts(right);
  // Distance from the side wall to the innermost blocking bench's TUCKED inner
  // edge — how far a back bench yields into the corner. A strictly-higher side
  // level always blocks; a tie blocks only when the side wins it. A side bench
  // whose span stops short of the back bench's band never collides in the corner.
  const yieldToSide = (arr: BenchLeg[], levels: number[], shifts: number[], lvl: number, corner: BenchCorner, backY0: number): number => {
    let reach = 0, start = 0;
    for (let i = 0; i < arr.length; i++) {
      const reaches = Math.min(arr[i].end, iy1) > backY0 + 1;
      const blocks = reaches && (levels[i] > lvl || (levels[i] === lvl && cornerTieWinner(inst, corner, lvl) === corner));
      if (blocks) reach = Math.max(reach, start + arr[i].depth - (shifts[i] ?? 0));
      start += arr[i].depth;
    }
    return reach;
  };
  // Depth of back the side wall must yield to, measured to the innermost blocking
  // back bench's TUCKED inner edge. A back bench whose span stops short of this
  // side tier's band never collides in the corner.
  const yieldToBack = (lvl: number, corner: BenchCorner, sideX: number): number => {
    let reach = 0, start = 0;
    for (let i = 0; i < back.length; i++) {
      const reaches = corner === 'left' ? Math.max(back[i].start, ix0) < sideX - 1 : Math.min(back[i].end, ix1) > sideX + 1;
      const blocks = reaches && (backLv[i] > lvl || (backLv[i] === lvl && cornerTieWinner(inst, corner, lvl) === 'back'));
      if (blocks) reach = Math.max(reach, start + back[i].depth - (backShift[i] ?? 0));
      start += back[i].depth;
    }
    return reach;
  };

  let cum = 0;
  back.forEach((leg, k) => {
    const y1 = iy1 - cum;
    const y0 = iy1 - cum - leg.depth;
    const x0 = Math.max(leg.start, ix0 + yieldToSide(left, leftLv, leftShift, backLv[k], 'left', y0));
    const x1 = Math.min(leg.end, ix1 - yieldToSide(right, rightLv, rightShift, backLv[k], 'right', y0));
    if (x1 - x0 > 1) out.push({ side: 'back', index: k, id: leg.id ?? '', leg, box: { x0, y0, x1, y1 }, fill: fillFor(leg, k, back.length), innerAxis: 'y', innerVal: y0 });
    cum += leg.depth;
  });
  cum = 0;
  left.forEach((leg, k) => {
    const x0 = ix0 + cum;
    const x1 = ix0 + cum + leg.depth;
    const end = Math.min(leg.end, iy1 - yieldToBack(leftLv[k], 'left', x1));
    if (end - leg.start > 1) out.push({ side: 'left', index: k, id: leg.id ?? '', leg, box: { x0, y0: leg.start, x1, y1: end }, fill: fillFor(leg, k, left.length), innerAxis: 'x', innerVal: x1 });
    cum += leg.depth;
  });
  cum = 0;
  right.forEach((leg, k) => {
    const x1 = ix1 - cum;
    const x0 = ix1 - cum - leg.depth;
    const end = Math.min(leg.end, iy1 - yieldToBack(rightLv[k], 'right', x0));
    if (end - leg.start > 1) out.push({ side: 'right', index: k, id: leg.id ?? '', leg, box: { x0, y0: leg.start, x1, y1: end }, fill: fillFor(leg, k, right.length), innerAxis: 'x', innerVal: x0 });
    cum += leg.depth;
  });
  return out;
}

/** All bench tier boxes (outer walls + inner walls), for snapping and editing. */
export function benchTierBoxes(inst: ProductInstance): TierBox[] {
  return tierBoxesOf(inst);
}

/** Local-frame corner points of every bench in a rectangular room, as snap
 *  targets (dimensions, bench editing). */
export function benchSnapCorners(inst: ProductInstance): Vec2[] {
  const out: Vec2[] = [];
  const push = (b: Box): void => {
    out.push({ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 });
  };
  for (const tb of tierBoxesOf(inst)) push(tb.box);
  for (const tb of innerWallTierBoxes(inst)) push(tb.box);
  return out;
}

/** Editable handles of a selected rectangular-room bench (local frame): the two
 *  end faces, the inner face, and the four corners. Mirrors polyBenchHandles. */
export interface RectBenchHandle {
  kind: 'start' | 'end' | 'inner' | 'corner-start-outer' | 'corner-start-inner' | 'corner-end-outer' | 'corner-end-inner';
  p: Vec2;
}

/** The edit frame of a rect-room bench: dir runs along the wall (toward growing
 *  span coordinate), normal points into the room; offsets measured from the wall. */
export function rectBenchFrame(inst: ProductInstance, benchId: string): { origin: Vec2; dir: Vec2; normal: Vec2; oOff: number; iOff: number; lo: number; hi: number; side: BenchSide } | null {
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const found = findBench(inst, benchId);
  if (!found) return null;
  const tiers = resolveTiers(inst, found.side);
  let cum = 0;
  for (let i = 0; i < found.index; i++) cum += tiers[i].depth;
  if (found.side === 'back') {
    return { origin: { x: 0, y: iy1 }, dir: { x: 1, y: 0 }, normal: { x: 0, y: -1 }, oOff: cum, iOff: cum + found.leg.depth, lo: ix0, hi: ix1, side: 'back' };
  }
  if (found.side === 'left') {
    return { origin: { x: ix0, y: 0 }, dir: { x: 0, y: 1 }, normal: { x: 1, y: 0 }, oOff: cum, iOff: cum + found.leg.depth, lo: iy0, hi: iy1, side: 'left' };
  }
  return { origin: { x: ix1, y: 0 }, dir: { x: 0, y: 1 }, normal: { x: -1, y: 0 }, oOff: cum, iOff: cum + found.leg.depth, lo: iy0, hi: iy1, side: 'right' };
}

export function rectBenchHandles(inst: ProductInstance, benchId: string): RectBenchHandle[] {
  const tb = tierBoxesOf(inst).find((t) => t.id === benchId);
  const fr = rectBenchFrame(inst, benchId);
  if (!tb || !fr) return [];
  const b = tb.box;
  // Express the box corners in the frame so 'start'/'end' track the span axis.
  const at = (t: number, off: number): Vec2 => ({ x: fr.origin.x + fr.dir.x * t + fr.normal.x * off, y: fr.origin.y + fr.dir.y * t + fr.normal.y * off });
  const t0 = fr.side === 'back' ? b.x0 : b.y0;
  const t1 = fr.side === 'back' ? b.x1 : b.y1;
  const mid = (t0 + t1) / 2;
  return [
    { kind: 'start', p: at(t0, (fr.oOff + fr.iOff) / 2) },
    { kind: 'end', p: at(t1, (fr.oOff + fr.iOff) / 2) },
    { kind: 'inner', p: at(mid, fr.iOff) },
    { kind: 'corner-start-outer', p: at(t0, fr.oOff) },
    { kind: 'corner-start-inner', p: at(t0, fr.iOff) },
    { kind: 'corner-end-outer', p: at(t1, fr.oOff) },
    { kind: 'corner-end-inner', p: at(t1, fr.iOff) },
  ];
}

/** Guide vertices for bench editing in a rectangular room (local frame): the
 *  four interior corners plus every other bench's corners. */
export function rectBenchGuideVerts(inst: ProductInstance, excludeBenchId: string): Vec2[] {
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const out: Vec2[] = [
    { x: ix0, y: iy0 }, { x: ix1, y: iy0 }, { x: ix1, y: iy1 }, { x: ix0, y: iy1 },
  ];
  const push = (b: Box): void => {
    out.push({ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 });
  };
  for (const tb of tierBoxesOf(inst)) if (tb.id !== excludeBenchId) push(tb.box);
  for (const tb of innerWallTierBoxes(inst)) if (tb.id !== excludeBenchId) push(tb.box);
  return out;
}

export interface BenchCornerJoint {
  corner: BenchCorner;
  /** Tier rank at which the two benches meet (0=low, 1=medium, 2=high). */
  level: number;
}

/** How far back along a bench (mm) a corner-join highlight / hit region reaches. */
const BENCH_CORNER_JOINT_REACH = 320;

const pointInBox = (p: Vec2, b: Box): boolean => p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1;

/**
 * The bench corner join (back meets left/right) under a local point, or null. A
 * join exists only where BOTH walls carry a tier at the same rank — a genuine tie
 * the user can swap — and the point falls in the near-corner end region of the
 * hovered bench (the same region that pulses green), so clicking the highlight
 * always swaps.
 */
export function benchCornerJointAt(inst: ProductInstance, p: Vec2): BenchCornerJoint | null {
  if (!usesLegModel(inst)) return null;
  const hit = benchHitAt(inst, p);
  if (!hit) return null;
  const tb = tierBoxesOf(inst).find((t) => t.side === hit.side && t.index === hit.index);
  if (!tb) return null;
  const back = resolveTiers(inst, 'back');
  const left = resolveTiers(inst, 'left');
  const right = resolveTiers(inst, 'right');
  const backLv = back.map((leg, k) => tierRank(effectiveTier(leg, k, back.length)));
  const leftLv = left.map((leg, k) => tierRank(effectiveTier(leg, k, left.length)));
  const rightLv = right.map((leg, k) => tierRank(effectiveTier(leg, k, right.length)));
  const R = BENCH_CORNER_JOINT_REACH;
  const cap = (corner: BenchCorner): Box => tierEndCap(tb.box, hit.side, corner, R);

  if (hit.side === 'back') {
    const lvl = backLv[hit.index];
    if (leftLv.includes(lvl) && pointInBox(p, cap('left'))) return { corner: 'left', level: lvl };
    if (rightLv.includes(lvl) && pointInBox(p, cap('right'))) return { corner: 'right', level: lvl };
    return null;
  }
  if (hit.side === 'left') {
    const lvl = leftLv[hit.index];
    return backLv.includes(lvl) && pointInBox(p, cap('left')) ? { corner: 'left', level: lvl } : null;
  }
  const lvl = rightLv[hit.index];
  return backLv.includes(lvl) && pointInBox(p, cap('right')) ? { corner: 'right', level: lvl } : null;
}

/** The near-corner end of a tier box, for the hover/swap highlight. */
function tierEndCap(box: Box, side: BenchSide, corner: BenchCorner, reach: number): Box {
  if (side === 'back') {
    return corner === 'left'
      ? { x0: box.x0, x1: Math.min(box.x1, box.x0 + reach), y0: box.y0, y1: box.y1 }
      : { x0: Math.max(box.x0, box.x1 - reach), x1: box.x1, y0: box.y0, y1: box.y1 };
  }
  return { x0: box.x0, x1: box.x1, y0: Math.max(box.y0, box.y1 - reach), y1: box.y1 }; // left/right tiers meet back at y1
}

/** Local-frame highlight boxes (both benches' ends) for a rectangular room's
 *  corner join, for the pulsing hover indicator. */
export function benchCornerJointHighlight(inst: ProductInstance, corner: BenchCorner, level: number): Box[] {
  const back = resolveTiers(inst, 'back');
  const side = resolveTiers(inst, corner);
  const backLv = back.map((leg, k) => tierRank(effectiveTier(leg, k, back.length)));
  const sideLv = side.map((leg, k) => tierRank(effectiveTier(leg, k, side.length)));
  const out: Box[] = [];
  for (const tb of tierBoxesOf(inst)) {
    if (tb.side === 'back' && backLv[tb.index] === level) out.push(tierEndCap(tb.box, 'back', corner, BENCH_CORNER_JOINT_REACH));
    else if (tb.side === corner && sideLv[tb.index] === level) out.push(tierEndCap(tb.box, corner, corner, BENCH_CORNER_JOINT_REACH));
  }
  return out;
}

/** A bench tier box anchored to an inner-wall face. */
export interface InnerTierBox {
  iwIndex: number;
  face: 'a' | 'b';
  index: number;
  id: string;
  leg: BenchLeg;
  box: Box;
  fill: string;
  innerAxis: 'x' | 'y';
  innerVal: number;
}

/** Distance from an inner-wall face to the interior wall on that side. */
function innerFaceAvail(inst: ProductInstance, iw: InnerWall, face: 'a' | 'b'): number {
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const h = wallThicknessOf(inst) / 2;
  const vertical = iw.wall === 'front' || iw.wall === 'back';
  if (vertical) return face === 'a' ? iw.pos - h - ix0 : ix1 - (iw.pos + h);
  return face === 'a' ? iw.pos - h - iy0 : iy1 - (iw.pos + h);
}

/**
 * The default run range (along the inner wall) a face bench occupies: from the
 * host wall's interior face to the wall tip, clamped to the room interior.
 */
export function innerWallRun(inst: ProductInstance, iw: InnerWall): [number, number] {
  const hw = inst.width / 2, hd = inst.depth / 2;
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const len = Math.max(0, iw.length);
  if (iw.wall === 'front') return [iy0, Math.min(iy1, -hd + len)];
  if (iw.wall === 'back') return [Math.max(iy0, hd - len), iy1];
  if (iw.wall === 'left') return [ix0, Math.min(ix1, -hw + len)];
  return [Math.max(ix0, hw - len), ix1]; // right
}

/**
 * Tier boxes for every inner-wall face bench. Tiers stack outward from the wall
 * face; each is clamped to the room interior. Inner-wall benches are independent
 * (no corner-meeting with the outer-wall benches).
 */
export function innerWallTierBoxes(inst: ProductInstance): InnerTierBox[] {
  const out: InnerTierBox[] = [];
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const h = wallThicknessOf(inst) / 2;
  const walls = inst.innerWalls || [];
  walls.forEach((iw, iwIndex) => {
    const vertical = iw.wall === 'front' || iw.wall === 'back';
    const [runLo, runHi] = innerWallRun(inst, iw);
    for (const face of ['a', 'b'] as const) {
      const tiers = iw.benches?.[face] ?? [];
      let cum = 0;
      tiers.forEach((leg, index) => {
        const a = Math.max(runLo, Math.min(leg.start, leg.end));
        const b = Math.min(runHi, Math.max(leg.start, leg.end));
        const depth = Math.max(0, leg.depth);
        let box: Box, innerAxis: 'x' | 'y', innerVal: number;
        if (vertical) {
          if (face === 'a') { // toward -x
            const x1 = Math.max(ix0, iw.pos - h - cum), x0 = Math.max(ix0, iw.pos - h - cum - depth);
            box = { x0, y0: a, x1, y1: b }; innerAxis = 'x'; innerVal = x0;
          } else { // toward +x
            const x0 = Math.min(ix1, iw.pos + h + cum), x1 = Math.min(ix1, iw.pos + h + cum + depth);
            box = { x0, y0: a, x1, y1: b }; innerAxis = 'x'; innerVal = x1;
          }
        } else {
          if (face === 'a') { // toward -y
            const y1 = Math.max(iy0, iw.pos - h - cum), y0 = Math.max(iy0, iw.pos - h - cum - depth);
            box = { x0: a, y0, x1: b, y1 }; innerAxis = 'y'; innerVal = y0;
          } else { // toward +y
            const y0 = Math.min(iy1, iw.pos + h + cum), y1 = Math.min(iy1, iw.pos + h + cum + depth);
            box = { x0: a, y0, x1: b, y1 }; innerAxis = 'y'; innerVal = y1;
          }
        }
        if (box.x1 - box.x0 > 1 && box.y1 - box.y0 > 1) {
          out.push({ iwIndex, face, index, id: leg.id ?? '', leg, box, fill: benchFill(inst, leg, index, tiers.length), innerAxis, innerVal });
        }
        cum += depth;
      });
    }
  });
  return out;
}

/**
 * Build the bench geometry from the per-side tier model. Each tier is emitted as
 * a polyline tagged with its fill colour and bench id. `holes` (e.g. grouped
 * heater footprints) and the steam cube are carved out of the benches.
 */
export function buildBenchGeometry(inst: ProductInstance, holes: Box[] = []): Geometry {
  const isSteam = inst.category === 'steamroom';
  const ents: GeomEntity[] = [];
  const feat = featureBox(inst);
  const emit = (box: Box, fill: string, id: string, _innerAxis: 'x' | 'y'): void => {
    let parts: Box[] = [box];
    if (feat) parts = parts.flatMap((b) => subtractBox(b, feat)); // steam cube: plain notch
    // Carve each heater's actual footprint (not a full-depth slot) so the bench
    // wraps around the heater on every overlapping side, like polygonal rooms.
    for (const hh of holes) parts = parts.flatMap((b) => subtractBox(b, hh));
    for (const b of parts) {
      if (b.x1 - b.x0 > 1 && b.y1 - b.y0 > 1) ents.push({ ...boxToPoly(b), color: fill, benchId: id });
    }
  };
  // A bench's overlap tucks IT under the bench toward the wall: the bench slides
  // wall-ward by its overlap so its wall-side band goes beneath the higher bench,
  // which (drawn last) covers it — so the lower bench's visible depth is
  // (depth - overlap) while the higher bench keeps its true size. The shift is
  // cumulative, so every bench inward stays adjacent to the one it tucks under.
  // The wall-touching tier (index 0) never tucks (nothing sits over it).
  const emitBench = (tb: TierBox, shift: number): void => {
    const leg = tb.leg;
    const yAxis = tb.innerAxis === 'y';
    const lo = yAxis ? tb.box.y0 : tb.box.x0;
    const hi = yAxis ? tb.box.y1 : tb.box.x1;
    const wall = Math.abs(tb.innerVal - lo) < Math.abs(tb.innerVal - hi) ? hi : lo; // wall edge = opposite the centre-facing edge
    const dir = Math.sign(tb.innerVal - wall) || 1; // wall → centre along the depth axis
    const d = -dir * shift; // translate this bench wall-ward by `shift`
    if (!leg.slats || leg.slats.length === 0) {
      const sb: Box = yAxis ? { ...tb.box, y0: tb.box.y0 + d, y1: tb.box.y1 + d } : { ...tb.box, x0: tb.box.x0 + d, x1: tb.box.x1 + d };
      emit(sb, tb.fill, tb.id, tb.innerAxis);
      return;
    }
    const spans = benchSlatSpans(leg);
    spans.forEach((s) => {
      const a = wall + dir * s.off + d, b = wall + dir * (s.off + s.width) + d;
      const d0 = Math.min(a, b), d1 = Math.max(a, b);
      const sb: Box = yAxis ? { x0: tb.box.x0, x1: tb.box.x1, y0: d0, y1: d1 } : { x0: d0, x1: d1, y0: tb.box.y0, y1: tb.box.y1 };
      emit(sb, tb.fill, tb.id, tb.innerAxis);
    });
  };
  const outerTiers = tierBoxesOf(inst);
  for (const side of BENCH_SIDES) {
    const group = outerTiers.filter((t) => t.side === side); // index-ordered 0 (wall) → N-1 (centre)
    // Cumulative wall-ward tuck: a lower bench runs under the higher one only when
    // that higher bench has a thickness (a void beneath its slab), else it butts.
    const shifts = benchStackShifts(group.map((t) => t.leg));
    for (let k = group.length - 1; k >= 0; k--) emitBench(group[k], shifts[k]); // centre first, wall last (so it covers the tuck)
  }
  for (const tb of innerWallTierBoxes(inst)) emit(tb.box, tb.fill, tb.id, tb.innerAxis);
  if (feat && isSteam) ents.push({ ...rect(feat.x0, feat.y0, feat.x1, feat.y1), color: FEATURE_COLOR });
  return { entities: ents, bounds: computeBounds(ents) };
}

/** A located bench tier in the model. */
export interface FoundBench {
  side: BenchSide;
  index: number;
  leg: BenchLeg;
}

/** Find a bench by id. */
export function findBench(inst: ProductInstance, id: string): FoundBench | null {
  if (!inst.benchTiers) return null;
  for (const side of BENCH_SIDES) {
    const arr = inst.benchTiers[side];
    if (!arr) continue;
    const index = arr.findIndex((t) => t.id === id);
    if (index >= 0) return { side, index, leg: arr[index] };
  }
  return null;
}

/** Hit-test the bench tiers; returns the bench under the point plus flags for
 *  proximity to its inner (room-facing) and outer (wall-facing) edges. */
export function benchHitAt(inst: ProductInstance, p: Vec2): { side: BenchSide; index: number; id: string; nearInner: boolean; nearOuter: boolean } | null {
  for (const tb of tierBoxesOf(inst)) {
    const b = tb.box;
    if (p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1) {
      const thr = Math.min(150, tb.leg.depth * 0.5);
      const coord = tb.innerAxis === 'y' ? p.y : p.x;
      const lo = tb.innerAxis === 'y' ? b.y0 : b.x0;
      const hi = tb.innerAxis === 'y' ? b.y1 : b.x1;
      // The outer (wall) face is the box edge opposite the inner (room) face.
      const outerVal = Math.abs(tb.innerVal - lo) < Math.abs(tb.innerVal - hi) ? hi : lo;
      const nearInner = Math.abs(coord - tb.innerVal) <= thr;
      const nearOuter = !nearInner && Math.abs(coord - outerVal) <= thr;
      return { side: tb.side, index: tb.index, id: tb.id, nearInner, nearOuter };
    }
  }
  return null;
}

/**
 * The long-side length (mm) of every uninterrupted bench slab in the room — the
 * tier boxes with the feature cube and grouped-heater holes carved out, exactly
 * as they're drawn.
 */
export function benchSlabLengths(inst: ProductInstance, holes: Box[] = []): number[] {
  const feat = featureBox(inst);
  const cutters = feat ? [feat] : [];
  const out: number[] = [];
  const tiers: Array<{ box: Box; innerAxis: 'x' | 'y' }> = [
    ...tierBoxesOf(inst).map((t) => ({ box: t.box, innerAxis: t.innerAxis })),
    ...innerWallTierBoxes(inst).map((t) => ({ box: t.box, innerAxis: t.innerAxis })),
  ];
  for (const tb of tiers) {
    let parts: Box[] = [tb.box];
    for (const h of cutters) parts = parts.flatMap((b) => subtractBox(b, h));
    // Heaters split the bench along its length only, so the run lengths (and thus
    // capacity) match the carved geometry rather than leaving short-side slivers.
    for (const h of holes) parts = parts.flatMap((b) => carveAcrossDepth(b, tb.innerAxis, h));
    for (const b of parts) {
      if (b.x1 - b.x0 > 1 && b.y1 - b.y0 > 1) out.push(Math.max(b.x1 - b.x0, b.y1 - b.y0));
    }
  }
  return out;
}

/** Total seating length (mm): the sum of every uninterrupted bench run. */
export function benchSeatingLength(inst: ProductInstance, holes: Box[] = []): number {
  return benchSlabLengths(inst, holes).reduce((s, l) => s + l, 0);
}

/** Capacity: one person per 60 cm of each uninterrupted bench run (floored per run). */
export function benchCapacity(inst: ProductInstance, holes: Box[] = []): number {
  return benchSlabLengths(inst, holes).reduce((c, l) => c + Math.floor(l / 600), 0);
}

/** The full (un-carved) box of a bench by id, for the selection highlight. */
export function benchBoxById(inst: ProductInstance, id: string): Box | null {
  for (const tb of tierBoxesOf(inst)) if (tb.id === id) return tb.box;
  for (const tb of innerWallTierBoxes(inst)) if (tb.id === id) return tb.box;
  return null;
}

let benchSeq = 0;
function newBenchId(): string {
  return `bench-${benchSeq++}-${Math.floor(performance.now())}`;
}

/**
 * Append a bench tier on a side, fitted between the walls and any perpendicular
 * benches, with a default depth clamped to the room. Returns the new bench id
 * (or '' when there's no room).
 */
/** A wall may hold at most this many bench tiers. */
export const MAX_BENCH_TIERS = 3;

export function addBenchTier(inst: ProductInstance, side: BenchSide, along?: number): string {
  ensureManaged(inst);
  if ((inst.benchTiers![side]?.length ?? 0) >= MAX_BENCH_TIERS) return ''; // cap at 3 tiers per wall
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const def = inst.category === 'steamroom' ? 500 : 600;
  let start: number;
  let end: number;
  let maxTotal: number;
  // Store the FULL interior span; the per-tier corner trimming (meeting
  // perpendicular benches by level: high↔high, medium↔medium, low↔low) is done
  // dynamically in tierBoxesOf, so it stays correct whichever wall is benched
  // first. Pre-subtracting the perpendicular depth here would over-clamp the
  // span and stop the tiers from extending to meet the corner.
  if (side === 'back') {
    start = ix0;
    end = ix1;
    maxTotal = iy1 - iy0 - 250;
  } else {
    start = iy0;
    end = iy1;
    const opp: BenchSide = side === 'left' ? 'right' : 'left';
    maxTotal = ix1 - ix0 - 250 - sideTotalDepth(inst, opp);
  }
  // A click on a wall split by an inner wall fills only the clicked segment.
  if (along != null) [start, end] = wallBenchSegment(inst, side, along);
  const remaining = maxTotal - sideTotalDepth(inst, side);
  if (remaining < MIN_BENCH_DEPTH || end - start < 100) return '';
  const depth = Math.max(MIN_BENCH_DEPTH, Math.min(def, remaining));
  const id = newBenchId();
  const tiers = [...(inst.benchTiers![side] ?? []), { id, depth, start, end }];
  inst.benchTiers = { ...inst.benchTiers, [side]: tiers };
  return id;
}

/** Remove a bench by id. */
export function removeBenchTier(inst: ProductInstance, id: string): void {
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        const bt = { ...inst.benchTiers };
        bt[side] = arr.filter((t) => t.id !== id);
        // Deleting a bench changes the tier counts, so re-apply the rules to the
        // whole room: drop manual tier overrides and let every remaining bench
        // take its automatic count-based tier (1 → low, 2 → medium+low, 3 → high…).
        for (const s of BENCH_SIDES) {
          const a = bt[s];
          if (a) bt[s] = a.map((t) => ({ ...t, tier: undefined }));
        }
        inst.benchTiers = bt;
        return;
      }
    }
  }
  // Inner-wall benches: drop the tier; clear the face/wall benches when empty.
  const loc = locateInner(inst, id);
  if (loc) {
    loc.arr.splice(loc.index, 1);
    loc.arr.forEach((t) => (t.tier = undefined)); // re-derive remaining tiers' levels
    if (loc.arr.length === 0) delete loc.iw.benches![loc.face];
    if (loc.iw.benches && !loc.iw.benches.a && !loc.iw.benches.b) loc.iw.benches = undefined;
  }
}

/** Set a bench's tier level (low/medium/high → colour). */
export function setBenchTierLevel(inst: ProductInstance, id: string, tier: BenchTier): void {
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => (t.id === id ? { ...t, tier } : t)) };
        return;
      }
    }
  }
  const loc = locateInner(inst, id);
  if (loc) loc.arr[loc.index].tier = tier;
}

/** Set a single bench's span (start/end along its wall), preserving depth/tier.
 *  Covers outer-wall benches and inner-wall face benches. */
export function setBenchLegSpan(inst: ProductInstance, id: string, start: number, end: number): void {
  const a = Math.min(start, end), b = Math.max(start, end);
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => (t.id === id ? { ...t, start: a, end: b } : t)) };
        return;
      }
    }
  }
  const loc = locateInner(inst, id);
  if (loc) { loc.arr[loc.index].start = a; loc.arr[loc.index].end = b; }
}

/** The edit frame of an inner-wall face bench: dir runs along the wall, normal
 *  points away from the bench's face; offsets measured from that face. */
export function innerBenchFrame(inst: ProductInstance, benchId: string): { origin: Vec2; dir: Vec2; normal: Vec2; oOff: number; iOff: number; lo: number; hi: number } | null {
  const found = findInnerBench(inst, benchId);
  const iw = found ? inst.innerWalls?.[found.iwIndex] : undefined;
  if (!found || !iw) return null;
  const h = wallThicknessOf(inst) / 2;
  const tiers = iw.benches?.[found.face] ?? [];
  let cum = 0;
  for (let i = 0; i < found.index; i++) cum += tiers[i].depth;
  const [lo, hi] = innerWallRun(inst, iw);
  const vertical = iw.wall === 'front' || iw.wall === 'back';
  if (vertical) {
    const base = found.face === 'a' ? iw.pos - h : iw.pos + h;
    const normal = found.face === 'a' ? { x: -1, y: 0 } : { x: 1, y: 0 };
    return { origin: { x: base, y: 0 }, dir: { x: 0, y: 1 }, normal, oOff: cum, iOff: cum + found.leg.depth, lo, hi };
  }
  const base = found.face === 'a' ? iw.pos - h : iw.pos + h;
  const normal = found.face === 'a' ? { x: 0, y: -1 } : { x: 0, y: 1 };
  return { origin: { x: 0, y: base }, dir: { x: 1, y: 0 }, normal, oOff: cum, iOff: cum + found.leg.depth, lo, hi };
}

/** Editable handles of a selected inner-wall bench, mirroring rectBenchHandles. */
export function innerBenchHandles(inst: ProductInstance, benchId: string): RectBenchHandle[] {
  const tb = innerWallTierBoxes(inst).find((t) => t.id === benchId);
  const fr = innerBenchFrame(inst, benchId);
  if (!tb || !fr) return [];
  const b = tb.box;
  const at = (t: number, off: number): Vec2 => ({ x: fr.origin.x + fr.dir.x * t + fr.normal.x * off, y: fr.origin.y + fr.dir.y * t + fr.normal.y * off });
  const alongY = fr.dir.y !== 0;
  const t0 = alongY ? b.y0 : b.x0;
  const t1 = alongY ? b.y1 : b.x1;
  const mid = (t0 + t1) / 2;
  return [
    { kind: 'start', p: at(t0, (fr.oOff + fr.iOff) / 2) },
    { kind: 'end', p: at(t1, (fr.oOff + fr.iOff) / 2) },
    { kind: 'inner', p: at(mid, fr.iOff) },
    { kind: 'corner-start-outer', p: at(t0, fr.oOff) },
    { kind: 'corner-start-inner', p: at(t0, fr.iOff) },
    { kind: 'corner-end-outer', p: at(t1, fr.oOff) },
    { kind: 'corner-end-inner', p: at(t1, fr.iOff) },
  ];
}

/** The space (mm) available for new tiers on an inner-wall face. */
export function innerFaceAvailable(inst: ProductInstance, iwIndex: number, face: 'a' | 'b'): number {
  const iw = inst.innerWalls?.[iwIndex];
  if (!iw) return 0;
  const used = (iw.benches?.[face] ?? []).reduce((s, t) => s + t.depth, 0);
  return innerFaceAvail(inst, iw, face) - used;
}

// ---- Inner-wall benches: tiers attached to either face of an interior wall ----

/** Locate an inner-wall bench tier by id (its inner wall, face, array and index). */
function locateInner(
  inst: ProductInstance,
  id: string
): { iw: InnerWall; face: 'a' | 'b'; arr: TierData[]; index: number } | null {
  for (const iw of inst.innerWalls || []) {
    if (!iw.benches) continue;
    for (const face of ['a', 'b'] as const) {
      const arr = iw.benches[face];
      if (!arr) continue;
      const index = arr.findIndex((t) => t.id === id);
      if (index >= 0) return { iw, face, arr, index };
    }
  }
  return null;
}

/** A located inner-wall bench tier. */
export interface FoundInnerBench { iwIndex: number; face: 'a' | 'b'; index: number; leg: TierData; }

/** Find an inner-wall bench by id. */
export function findInnerBench(inst: ProductInstance, id: string): FoundInnerBench | null {
  const walls = inst.innerWalls || [];
  for (let iwIndex = 0; iwIndex < walls.length; iwIndex++) {
    const iw = walls[iwIndex];
    if (!iw.benches) continue;
    for (const face of ['a', 'b'] as const) {
      const arr = iw.benches[face];
      const index = arr ? arr.findIndex((t) => t.id === id) : -1;
      if (arr && index >= 0) return { iwIndex, face, index, leg: arr[index] };
    }
  }
  return null;
}

/**
 * Append a bench tier to one face of an inner wall, stacking outward with a
 * default depth clamped to the available room. Returns the new bench id (or '').
 */
export function addInnerWallBench(inst: ProductInstance, iwIndex: number, face: 'a' | 'b'): string {
  const iw = inst.innerWalls?.[iwIndex];
  if (!iw) return '';
  const existing = iw.benches?.[face] ?? [];
  if (existing.length >= MAX_BENCH_TIERS) return '';
  const used = existing.reduce((s, t) => s + t.depth, 0);
  const avail = innerFaceAvail(inst, iw, face) - used;
  if (avail < MIN_BENCH_DEPTH) return '';
  const [runLo, runHi] = innerWallRun(inst, iw);
  if (runHi - runLo < 100) return '';
  const def = inst.category === 'steamroom' ? 500 : 600;
  const depth = Math.max(MIN_BENCH_DEPTH, Math.min(def, avail));
  if (!iw.id) iw.id = newBenchId();
  if (!iw.benches) iw.benches = {};
  const id = newBenchId();
  iw.benches[face] = [...existing, { id, depth, start: runLo, end: runHi }];
  return id;
}

/** Hit-test inner-wall bench tiers; returns the bench under the point (+ edge flag). */
export function innerBenchHitAt(
  inst: ProductInstance,
  p: Vec2
): { iwIndex: number; face: 'a' | 'b'; index: number; id: string; nearInner: boolean } | null {
  for (const tb of innerWallTierBoxes(inst)) {
    const b = tb.box;
    if (p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1) {
      const thr = Math.min(150, tb.leg.depth * 0.5);
      const nearInner = tb.innerAxis === 'y' ? Math.abs(p.y - tb.innerVal) <= thr : Math.abs(p.x - tb.innerVal) <= thr;
      return { iwIndex: tb.iwIndex, face: tb.face, index: tb.index, id: tb.id, nearInner };
    }
  }
  return null;
}

/**
 * Coordinates (along a side's span axis) where a bench end can butt up against an
 * inner wall: an inner wall that crosses the bench's span contributes both of its
 * faces (pos ± half thickness). A back bench (spanning x) meets the vertical inner
 * walls hosted on the front/back; a left/right bench (spanning y) meets the
 * horizontal inner walls hosted on the left/right.
 */
export function innerWallSnapCoords(inst: ProductInstance, side: BenchSide): number[] {
  const h = wallThicknessOf(inst) / 2;
  const out: number[] = [];
  for (const iw of inst.innerWalls || []) {
    const vertical = iw.wall === 'front' || iw.wall === 'back'; // strip at constant x
    const crosses = side === 'back' ? vertical : !vertical; // back spans x; sides span y
    if (crosses) out.push(iw.pos - h, iw.pos + h);
  }
  return out;
}

/**
 * The span (along its wall) of the bench segment containing `along`, bounded by
 * any inner walls that actually meet that wall and divide it. With no
 * interrupting inner wall this is the full interior span. Used so clicking to add
 * a bench fills only the wall segment on the clicked side of an inner wall.
 */
export function wallBenchSegment(inst: ProductInstance, side: BenchSide, along: number): [number, number] {
  const { ix0, ix1, iy0, iy1 } = benchInterior(inst);
  const h = wallThicknessOf(inst) / 2, eps = 1;
  let lo = side === 'back' ? ix0 : iy0;
  let hi = side === 'back' ? ix1 : iy1;
  for (const iw of inst.innerWalls || []) {
    const vertical = iw.wall === 'front' || iw.wall === 'back';
    const crosses = side === 'back' ? vertical : !vertical; // must cross this wall's span
    if (!crosses) continue;
    const len = Math.max(0, iw.length);
    // The inner wall divides this wall only if it actually reaches it: a wall
    // hosted on this side always does; one on the opposite side must span fully.
    const reaches =
      side === 'back' ? iw.wall === 'back' || (iw.wall === 'front' && len >= inst.depth - eps)
        : side === 'left' ? iw.wall === 'left' || (iw.wall === 'right' && len >= inst.width - eps)
          : iw.wall === 'right' || (iw.wall === 'left' && len >= inst.width - eps);
    if (!reaches) continue;
    const c0 = iw.pos - h, c1 = iw.pos + h; // the inner wall's footprint along the span
    if (c1 <= along && c1 > lo) lo = c1; // inner wall is below the click → raise the lower bound
    if (c0 >= along && c0 < hi) hi = c0; // inner wall is above the click → lower the upper bound
  }
  return [lo, hi];
}

/** Set a single bench's depth. */
export function setBenchLegDepth(inst: ProductInstance, id: string, depth: number): void {
  const d = Math.max(MIN_BENCH_DEPTH, depth);
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => (t.id === id ? { ...t, depth: d } : t)) };
        return;
      }
    }
  }
  const loc = locateInner(inst, id);
  if (loc) loc.arr[loc.index].depth = d;
}

/**
 * Set a single bench's slat composition (widths + gaps + overlap + preset id).
 * When slats are given the bench depth is driven by them (Σ slats + Σ gaps).
 * Passing `slats: undefined` clears the slats and leaves the depth as-is.
 */
export function setBenchLegSlats(
  inst: ProductInstance,
  id: string,
  patch: { slats?: number[]; gaps?: number[]; slatThickness?: number[]; overlap?: number; slatPresetId?: string },
): void {
  const apply = (t: BenchLeg): BenchLeg => {
    const next: BenchLeg = { ...t };
    if ('slats' in patch) next.slats = patch.slats ? [...patch.slats] : undefined;
    if ('gaps' in patch) next.gaps = patch.gaps ? [...patch.gaps] : undefined;
    if ('slatThickness' in patch) next.slatThickness = patch.slatThickness ? [...patch.slatThickness] : undefined;
    if ('overlap' in patch) next.overlap = patch.overlap;
    if ('slatPresetId' in patch) next.slatPresetId = patch.slatPresetId;
    const d = benchDepthFromSlats(next);
    if (d != null) next.depth = Math.max(MIN_BENCH_DEPTH, d);
    return next;
  };
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => (t.id === id ? apply(t) : t)) };
        return;
      }
    }
  }
  // Inner-wall benches (BenchTier) don't carry slats; only the derived depth is
  // meaningful, so apply and coerce back to the tier type.
  const loc = locateInner(inst, id);
  if (loc) loc.arr[loc.index] = { ...loc.arr[loc.index], ...apply(loc.arr[loc.index]), id: loc.arr[loc.index].id };
}

/** Set (or clear, with undefined) a single bench's fill colour. */
export function setBenchLegColor(inst: ProductInstance, id: string, color: string | undefined): void {
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr && arr.some((t) => t.id === id)) {
        inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => (t.id === id ? { ...t, color } : t)) };
        return;
      }
    }
  }
  const loc = locateInner(inst, id);
  if (loc) loc.arr[loc.index].color = color;
}

/**
 * Rescale the customised bench model when the room footprint changes: a bench's
 * LENGTH (span along its wall) follows the wall it sits on, while its DEPTH
 * (toward the centre) is kept constant. Works in interior dimensions (footprint
 * minus the two walls), so benches stay within the walls. Auto-layout rooms need
 * no scaling — they re-derive their benches from the new size.
 */
export function scaleBenchTiers(inst: ProductInstance, oldW: number, oldD: number, newW: number, newD: number): void {
  const wall = wallThicknessOf(inst);
  const oldIw = oldW - 2 * wall, oldId = oldD - 2 * wall;
  const newIw = newW - 2 * wall, newId = newD - 2 * wall;
  if (oldIw <= 0 || oldId <= 0 || newIw <= 0 || newId <= 0) return;
  // Interior is centred on the local origin, so a proportional remap of a span
  // coordinate is a plain multiply by the interior-dimension ratio.
  const rW = newIw / oldIw, rH = newId / oldId;
  const oIx0 = -oldIw / 2, oIx1 = oldIw / 2, oIy0 = -oldId / 2, oIy1 = oldId / 2;
  const nIx0 = -newIw / 2, nIx1 = newIw / 2, nIy0 = -newId / 2, nIy1 = newId / 2;

  // An endpoint that *meets* something — a wall, or the face of a perpendicular
  // bench — must keep meeting it after the resize. Those meetings sit at a fixed
  // distance from a wall (0 for the wall itself, or a perpendicular bench's
  // depth, which is unchanged by the resize). So a meeting endpoint keeps its
  // distance from the (moved) wall; only a free endpoint scales with the span.
  const totalDepth = (side: BenchSide): number => resolveTiers(inst, side).reduce((s, t) => s + t.depth, 0);
  const firstDepth = (side: BenchSide): number => resolveTiers(inst, side)[0]?.depth ?? 0;
  const MEET_EPS = 1.5; // mm: meeting positions are set programmatically, so exact
  const meets = (dist: number, depths: number[]): boolean => depths.some((d) => Math.abs(dist - d) < MEET_EPS);

  const scaleLeg = (side: BenchSide, leg: { depth: number; start: number; end: number }): void => {
    if (side === 'back') {
      // start is the left end (near ix0); end is the right end (near ix1).
      const dStart = leg.start - oIx0; // distance from the left wall
      const dEnd = oIx1 - leg.end; // distance from the right wall
      leg.start = meets(dStart, [0, totalDepth('left')]) ? nIx0 + dStart : leg.start * rW;
      leg.end = meets(dEnd, [0, totalDepth('right')]) ? nIx1 - dEnd : leg.end * rW;
    } else {
      // start is the front end (near iy0); end is the back end (near iy1, may
      // meet the back bench at its first-tier or full depth).
      const dStart = leg.start - oIy0; // distance from the front wall
      const dEnd = oIy1 - leg.end; // distance from the back wall
      leg.start = meets(dStart, [0]) ? nIy0 + dStart : leg.start * rH;
      leg.end = meets(dEnd, [0, firstDepth('back'), totalDepth('back')]) ? nIy1 - dEnd : leg.end * rH;
    }
  };
  if (inst.benchTiers) {
    for (const side of BENCH_SIDES) {
      const arr = inst.benchTiers[side];
      if (arr) for (const leg of arr) scaleLeg(side, leg);
    }
  }
  if (inst.benchEdits) {
    for (const side of BENCH_SIDES) {
      const leg = inst.benchEdits[side];
      if (leg) scaleLeg(side, leg);
    }
  }
  if (inst.cubePos) inst.cubePos = { x: inst.cubePos.x * rW, y: inst.cubePos.y * rH };
}

// ---- Legacy per-side helpers (used by the properties panel + bench drag) ----

/** Add a full bench on a side (a new tier). */
export function addBench(inst: ProductInstance, side: BenchSide): void {
  addBenchTier(inst, side);
}

/** Remove all benches on a side. */
export function removeBench(inst: ProductInstance, side: BenchSide): void {
  ensureManaged(inst);
  inst.benchTiers = { ...inst.benchTiers, [side]: [] };
}

/** Set the depth of every tier on a side. */
export function setBenchDepth(inst: ProductInstance, side: BenchSide, depth: number): void {
  ensureManaged(inst);
  const arr = inst.benchTiers![side] ?? [];
  inst.benchTiers = { ...inst.benchTiers, [side]: arr.map((t) => ({ ...t, depth: Math.max(MIN_BENCH_DEPTH, depth) })) };
}

/** Replace a side with a single custom-span bench (from a bench-tool drag). */
export function setBenchSpan(inst: ProductInstance, side: BenchSide, start: number, end: number, depth?: number): void {
  ensureManaged(inst);
  const d = depth ?? (inst.category === 'steamroom' ? 500 : 600);
  const id = newBenchId();
  inst.benchTiers = {
    ...inst.benchTiers,
    [side]: [{ id, depth: Math.max(MIN_BENCH_DEPTH, d), start: Math.min(start, end), end: Math.max(start, end) }],
  };
}

/** Which side's bench (if any) contains the given local point. */
export function benchSideAt(inst: ProductInstance, p: Vec2): BenchSide | null {
  return benchHitAt(inst, p)?.side ?? null;
}

/** The local-frame rectangle of a tentative bench on a side (for live preview). */
export function benchLegRect(inst: ProductInstance, side: BenchSide, start: number, end: number, depth?: number): Vec2[] {
  const { ix0, ix1, iy1 } = benchInterior(inst);
  const d = depth ?? resolveLegs(inst)[side]?.depth ?? (inst.category === 'steamroom' ? 500 : 600);
  const a = Math.min(start, end);
  const b = Math.max(start, end);
  if (side === 'back') return [{ x: a, y: iy1 - d }, { x: b, y: iy1 - d }, { x: b, y: iy1 }, { x: a, y: iy1 }];
  if (side === 'left') return [{ x: ix0, y: a }, { x: ix0 + d, y: a }, { x: ix0 + d, y: b }, { x: ix0, y: b }];
  return [{ x: ix1 - d, y: a }, { x: ix1, y: a }, { x: ix1, y: b }, { x: ix1 - d, y: b }];
}
